import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { useUser } from './UserContext';
import { galleryService } from '../services/api/galleryService';
import type {
  GalleryProfileCard,
  GalleryFilter,
  GalleryLayoutVariant,
  GalleryPaginationState,
  SavedGalleryConfig,
  CreateSavedGalleryRequest,
} from '../types/gallery';
import { DEFAULT_PAGE_SIZE } from '../types/gallery';

// ==========================================
// TYPES
// ==========================================

interface GalleryState {
  // Data
  profiles: GalleryProfileCard[];
  pagination: GalleryPaginationState;

  // Loading states
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;

  // Filter & Layout
  filters: GalleryFilter;
  layout: GalleryLayoutVariant;

  // Saved galleries
  savedGalleries: SavedGalleryConfig[];
  activeGalleryId: string | null;

  // View mode (for Discovery page toggle)
  viewMode: 'gallery' | 'swipe';
}

interface GalleryContextValue extends GalleryState {
  // Data Actions
  loadGallery: (page?: number) => Promise<void>;
  loadNextPage: () => Promise<void>;
  loadPreviousPage: () => Promise<void>;
  goToPage: (page: number) => Promise<void>;
  refreshGallery: () => Promise<void>;

  // Filter Actions
  setFilters: (filters: Partial<GalleryFilter>) => void;
  resetFilters: () => void;
  applyFilters: () => Promise<void>;

  // Layout Actions
  setLayout: (layout: GalleryLayoutVariant) => void;

  // View Mode Actions
  setViewMode: (mode: 'gallery' | 'swipe') => void;
  toggleViewMode: () => void;

  // Saved Gallery Actions
  loadSavedGalleries: () => Promise<void>;
  saveCurrentAsGallery: (name: string, description?: string) => Promise<SavedGalleryConfig>;
  loadSavedGallery: (id: string) => Promise<void>;
  deleteSavedGallery: (id: string) => Promise<void>;
  updateSavedGallery: (
    id: string,
    updates: Partial<SavedGalleryConfig>
  ) => Promise<SavedGalleryConfig>;

  // Profile Actions
  onProfileClick: (profileId: string, position: number) => void;
  startSwipeFromProfile: (profileId: string) => void;
}

const GalleryContext = createContext<GalleryContextValue | null>(null);

// ==========================================
// INITIAL STATE
// ==========================================

const initialPagination: GalleryPaginationState = {
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE || 20,
  totalPages: 0,
  totalCount: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const defaultFilters: GalleryFilter = {
  minAge: 18,
  maxAge: 99,
  maxDistanceKm: 100,
  sortBy: 'recommended',
  sortOrder: 'desc',
};

// ==========================================
// PROVIDER
// ==========================================

interface GalleryProviderProps {
  children: ReactNode;
  initialLayout?: GalleryLayoutVariant;
  onStartSwipe?: (profileId: string) => void;
}

export function GalleryProvider({
  children,
  initialLayout = 'grid',
  onStartSwipe,
}: GalleryProviderProps) {
  const { user } = useUser();

  // Data State
  const [profiles, setProfiles] = useState<GalleryProfileCard[]>([]);
  const [pagination, setPagination] = useState<GalleryPaginationState>(initialPagination);

  // Loading State
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter & Layout State
  const [filters, setFiltersState] = useState<GalleryFilter>(defaultFilters);
  const [layout, setLayoutState] = useState<GalleryLayoutVariant>(initialLayout);

  // Saved Galleries State
  const [savedGalleries, setSavedGalleries] = useState<SavedGalleryConfig[]>([]);
  const [activeGalleryId, setActiveGalleryId] = useState<string | null>(null);

  // View Mode State
  const [viewMode, setViewModeState] = useState<'gallery' | 'swipe'>('gallery');

  // ==========================================
  // DATA LOADING
  // ==========================================

  const loadGallery = useCallback(
    async (page: number = 1) => {
      if (!user) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await galleryService.getGallery(filters, page, pagination.pageSize);
        setProfiles(response.profiles);
        setPagination(response.pagination);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load gallery');
        console.error('Failed to load gallery:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [user, filters, pagination.pageSize]
  );

  const loadNextPage = useCallback(async () => {
    if (!pagination.hasNextPage || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await galleryService.getGallery(
        filters,
        pagination.currentPage + 1,
        pagination.pageSize
      );
      setProfiles(response.profiles);
      setPagination(response.pagination);

      // Track analytics
      galleryService.trackPageChange(pagination.currentPage + 1, layout, 'next');
    } catch (err) {
      console.error('Failed to load next page:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [pagination, filters, isLoadingMore, layout]);

  const loadPreviousPage = useCallback(async () => {
    if (!pagination.hasPreviousPage || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const response = await galleryService.getGallery(
        filters,
        pagination.currentPage - 1,
        pagination.pageSize
      );
      setProfiles(response.profiles);
      setPagination(response.pagination);

      // Track analytics
      galleryService.trackPageChange(pagination.currentPage - 1, layout, 'previous');
    } catch (err) {
      console.error('Failed to load previous page:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [pagination, filters, isLoadingMore, layout]);

  const goToPage = useCallback(
    async (page: number) => {
      if (page < 1 || page > pagination.totalPages || isLoadingMore) return;

      setIsLoadingMore(true);
      try {
        const response = await galleryService.getGallery(filters, page, pagination.pageSize);
        setProfiles(response.profiles);
        setPagination(response.pagination);

        // Track analytics
        galleryService.trackPageChange(page, layout, 'direct');
      } catch (err) {
        console.error('Failed to go to page:', err);
      } finally {
        setIsLoadingMore(false);
      }
    },
    [pagination.totalPages, pagination.pageSize, filters, isLoadingMore, layout]
  );

  const refreshGallery = useCallback(async () => {
    await loadGallery(1);
  }, [loadGallery]);

  // ==========================================
  // FILTER ACTIONS
  // ==========================================

  const setFilters = useCallback((newFilters: Partial<GalleryFilter>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(defaultFilters);
    setActiveGalleryId(null);
  }, []);

  const applyFilters = useCallback(async () => {
    // Track filter change
    galleryService.trackFilterChange(filters, layout);
    await loadGallery(1);
  }, [loadGallery, filters, layout]);

  // ==========================================
  // LAYOUT ACTIONS
  // ==========================================

  const setLayout = useCallback(
    (newLayout: GalleryLayoutVariant) => {
      setLayoutState(newLayout);
      // Persist to localStorage
      localStorage.setItem('gallery-layout-preference', newLayout);
      // Track layout view
      galleryService.trackLayoutView(newLayout, user?.id);
    },
    [user?.id]
  );

  // ==========================================
  // VIEW MODE ACTIONS
  // ==========================================

  const setViewMode = useCallback((mode: 'gallery' | 'swipe') => {
    setViewModeState(mode);
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewModeState((prev) => (prev === 'gallery' ? 'swipe' : 'gallery'));
  }, []);

  // ==========================================
  // SAVED GALLERY ACTIONS
  // ==========================================

  const loadSavedGalleries = useCallback(async () => {
    if (!user) return;

    try {
      const galleries = await galleryService.getSavedGalleries();
      setSavedGalleries(galleries);
    } catch (err) {
      console.error('Failed to load saved galleries:', err);
    }
  }, [user]);

  const saveCurrentAsGallery = useCallback(
    async (name: string, description?: string): Promise<SavedGalleryConfig> => {
      const request: CreateSavedGalleryRequest = {
        name,
        description,
        filters,
        layout,
      };

      const config = await galleryService.createSavedGallery(request);
      setSavedGalleries((prev) => [...prev, config]);
      setActiveGalleryId(config.id);
      return config;
    },
    [filters, layout]
  );

  const loadSavedGallery = useCallback(
    async (id: string) => {
      // First check local cache
      let config = savedGalleries.find((g) => g.id === id);

      // If not in cache, fetch from server
      if (!config) {
        config = (await galleryService.getSavedGallery(id)) || undefined;
      }

      if (config) {
        setFiltersState(config.filters);
        setLayoutState(config.layout);
        setActiveGalleryId(config.id);
        await loadGallery(1);
      }
    },
    [savedGalleries, loadGallery]
  );

  const deleteSavedGallery = useCallback(
    async (id: string) => {
      const success = await galleryService.deleteSavedGallery(id);
      if (success) {
        setSavedGalleries((prev) => prev.filter((g) => g.id !== id));
        if (activeGalleryId === id) {
          setActiveGalleryId(null);
        }
      }
    },
    [activeGalleryId]
  );

  const updateSavedGallery = useCallback(
    async (id: string, updates: Partial<SavedGalleryConfig>): Promise<SavedGalleryConfig> => {
      const updated = await galleryService.updateSavedGallery(id, updates);
      setSavedGalleries((prev) => prev.map((g) => (g.id === id ? updated : g)));
      return updated;
    },
    []
  );

  // ==========================================
  // PROFILE ACTIONS
  // ==========================================

  const onProfileClick = useCallback(
    (profileId: string, position: number) => {
      // Track click analytics
      galleryService.trackProfileClick(profileId, layout, position);
    },
    [layout]
  );

  const startSwipeFromProfile = useCallback(
    (profileId: string) => {
      // If callback provided, use it
      if (onStartSwipe) {
        onStartSwipe(profileId);
      }
      // Switch to swipe mode
      setViewModeState('swipe');
    },
    [onStartSwipe]
  );

  // ==========================================
  // EFFECTS
  // ==========================================

  // Load gallery on mount and when user changes
  useEffect(() => {
    if (user) {
      loadGallery();
      loadSavedGalleries();
    } else {
      setProfiles([]);
      setPagination(initialPagination);
      setSavedGalleries([]);
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load layout preference from localStorage
  useEffect(() => {
    const savedLayout = localStorage.getItem('gallery-layout-preference') as GalleryLayoutVariant;
    if (savedLayout && ['grid', 'row', 'list'].includes(savedLayout)) {
      setLayoutState(savedLayout);
    }
  }, []);

  // ==========================================
  // CONTEXT VALUE
  // ==========================================

  const value: GalleryContextValue = useMemo(
    () => ({
      // State
      profiles,
      pagination,
      isLoading,
      isLoadingMore,
      error,
      filters,
      layout,
      savedGalleries,
      activeGalleryId,
      viewMode,

      // Data Actions
      loadGallery,
      loadNextPage,
      loadPreviousPage,
      goToPage,
      refreshGallery,

      // Filter Actions
      setFilters,
      resetFilters,
      applyFilters,

      // Layout Actions
      setLayout,

      // View Mode Actions
      setViewMode,
      toggleViewMode,

      // Saved Gallery Actions
      loadSavedGalleries,
      saveCurrentAsGallery,
      loadSavedGallery,
      deleteSavedGallery,
      updateSavedGallery,

      // Profile Actions
      onProfileClick,
      startSwipeFromProfile,
    }),
    [
      profiles,
      pagination,
      isLoading,
      isLoadingMore,
      error,
      filters,
      layout,
      savedGalleries,
      activeGalleryId,
      viewMode,
      loadGallery,
      loadNextPage,
      loadPreviousPage,
      goToPage,
      refreshGallery,
      setFilters,
      resetFilters,
      applyFilters,
      setLayout,
      setViewMode,
      toggleViewMode,
      loadSavedGalleries,
      saveCurrentAsGallery,
      loadSavedGallery,
      deleteSavedGallery,
      updateSavedGallery,
      onProfileClick,
      startSwipeFromProfile,
    ]
  );

  return <GalleryContext.Provider value={value}>{children}</GalleryContext.Provider>;
}

// ==========================================
// HOOK
// ==========================================

export function useGallery(): GalleryContextValue {
  const context = useContext(GalleryContext);
  if (!context) {
    throw new Error('useGallery must be used within a GalleryProvider');
  }
  return context;
}

export default GalleryContext;
